import { createContext, useContext, ReactNode, useMemo } from 'react'
import { useCategories as useCategoriesQuery } from '@/lib/queries/useCategories'

interface Category {
  id: string
  name_en: string
}

interface CategoriesContextType {
  categories: Category[]
  categoriesMap: Record<string, string>
  isLoading: boolean
  error: Error | null
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined)

export function CategoriesProvider({ children }: { children: ReactNode }) {
  const { data: categories = [], isLoading, error } = useCategoriesQuery()

  const categoriesMap = useMemo(
    () => Object.fromEntries(categories.map(c => [c.id, c.name_en])),
    [categories]
  )

  const value = useMemo(
    () => ({
      categories,
      categoriesMap,
      isLoading,
      error: error as Error | null,
    }),
    [categories, categoriesMap, isLoading, error]
  )

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  )
}

export function useCategories() {
  const context = useContext(CategoriesContext)
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoriesProvider')
  }
  return context
}
